/**
 * Scans that are waiting for the signal, kept so that leaving the screen does
 * not lose them.
 *
 * A scan that got no answer stays on its fork, asked about again when the
 * signal returns (see lookupRun.ts). That lives in the screen, and a screen's
 * state goes when the operator taps away or the page reloads. So while
 * anything is waiting, the screen's slots are written here, and read back
 * when it opens.
 *
 * These are scans, not work done: nothing here has been sent or accepted, and
 * none of it is in the sync queue. They belong to the operator who scanned
 * them, so a session boundary clears them with everything else
 * (useSessionBoundary). A tablet that restarts with no signal comes back on
 * the sign-in screen and starts clean — decided 2026-10-08.
 *
 * localStorage, not IndexedDB: it is a handful of short strings, read once
 * synchronously as a screen mounts.
 */

const KEY = 'dockautomation.heldScans'

type Kept = Record<string, Record<string, string>>

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function storage(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function read(store: Store): Kept {
  try {
    const parsed: unknown = JSON.parse(store.getItem(KEY) ?? '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Kept : {}
  } catch {
    return {}
  }
}

/** Keep one screen's slots. Empty slots are left out; all empty removes the screen's entry. */
export function saveHeldScans(screen: string, slots: Record<string, string>, store: Store | null = storage()): void {
  if (!store) return
  const filled = Object.fromEntries(Object.entries(slots).filter(([, code]) => typeof code === 'string' && code !== ''))
  const all = read(store)
  if (Object.keys(filled).length === 0) Reflect.deleteProperty(all, screen)
  else all[screen] = filled
  try {
    if (Object.keys(all).length === 0) store.removeItem(KEY)
    else store.setItem(KEY, JSON.stringify(all))
  } catch {
    // No room, or no storage: the scans still wait on the screen itself.
  }
}

/** What one screen kept, slot by slot; empty when it kept nothing. */
export function loadHeldScans(screen: string, store: Store | null = storage()): Record<string, string> {
  if (!store) return {}
  const kept = read(store)[screen]
  if (!kept || typeof kept !== 'object') return {}
  return Object.fromEntries(Object.entries(kept).filter(([, code]) => typeof code === 'string' && code !== ''))
}

/** Whether any screen has scans waiting. A page reload would lose its place in them. */
export function anyHeldScans(store: Store | null = storage()): boolean {
  return store !== null && Object.keys(read(store)).length > 0
}

/** Forget one screen's kept scans, or every screen's when none is named. */
export function clearHeldScans(screen?: string, store: Store | null = storage()): void {
  if (!store) return
  if (screen === undefined) {
    try {
      store.removeItem(KEY)
    } catch {
      // Nothing to forget.
    }
    return
  }
  saveHeldScans(screen, {}, store)
}
